import { destroy, getSnapshot, types } from "mobx-state-tree";
import { CourseStore as Course } from "./CourseStore";
import { ChapterStore as Chapter } from "./ChapterStore";
import { LessonStore as Lesson } from "./Lesson/LessonStore";
import { UserStore as User } from "./UserStore";
import { ReviewStore as Review } from "./ReviewStore";
import { CommentStore as Comment } from "./CommentStore";
import { ModalStore as Modal } from "./ModalStore";
import _ from "lodash";

export const AppStore = types
  .model({
    /**
     * Course lessons
     */
    lessons: types.array(Lesson),

    /**
     * Current lesson
     */
    currentTab: types.optional(types.enumeration(["curriculum", "lesson", "comment"]), "lesson"),
    /**
     * Current lesson
     */
    currentLesson: types.maybeNull(types.safeReference(types.late(() => Lesson))),
    /**
     * Current time for Lesson video
     */
    currentTime: types.optional(types.number, 0),

    /**
     * Course chapters
     */
    chapters: types.array(Chapter),

    /**
     * Course details
     */
    course: types.maybeNull(Course),

    /**
     * User information
     */
    user: types.maybeNull(User),

    /**
     * Comment details
     */
    comments: types.array(Comment),

    /**
     * Configure the visual UI shown to the user
     */
    permissions: types.array(types.string),

    /**
     * Configure the visual UI shown to the user
     */
    review: types.maybeNull(Review),

    /**
     * Loading of lesson
     */
    isLessonLoading: types.optional(types.boolean, false),

    /**
     * Report modal
     */
    reportModal: types.optional(Modal, {}),
    /**
     * Rating modal
     */
    ratingModal: types.optional(Modal, {}),

    /**
     * Chapter create/update modal
     */
    chapterModal: types.optional(Modal, {}),
    /**
     * Lesson create/update modal
     */
    lessonModal: types.optional(Modal, {}),
    /**
     * Comment create/update modal
     */
    commentModal: types.optional(Modal, {}),
    /**
     * Reaction modal
     */
    reactionModal: types.optional(Modal, {}),
    /**
     * Comment create/update modal
     */
    productDetailModal: types.optional(Modal, {}),
    /**
     * Add to Cart CTA modal
     */
    addToCartModal: types.optional(Modal, {}),
    /**
     * Question delete modal
     */
    questionModal: types.optional(Modal, {}),
  })
  .views((self) => ({
    get progress() {
      const lessonDoneCount = self.chapters.reduce((sum, chapter) => sum + chapter.progress, 0);

      return lessonDoneCount / self.lessons.length;
    },

    // Seconds to Percentage
    get currentLessonProgress() {
      return _.round((self.currentTime / self.currentLesson.video.duration) * 100, 0);
    },
  }))
  .actions((self) => ({
    /**
     * Check for user permission
     * @param {string} name
     * @return {boolean}
     */
    hasPermission(name) {
      return self.permissions.find((i) => name === i) !== undefined;
    },

    setTab(tab) {
      self.currentTab = tab;
    },

    setReview(review) {
      self.review = review;
    },

    setCurrentLesson(lessonId) {
      if (self.lessons.find((lesson) => lesson.id === lessonId)) {
        self.currentLesson = lessonId;
        return true;
      }
      return false;
    },

    setCurrentTime(time) {
      self.currentTime = time;
    },

    /**
     * Chapter model Actions
     */
    createChapter(chapterData) {
      const newChapter = Chapter.create(chapterData);

      self.chapters.push(newChapter);
    },

    updateChapter(newData) {
      const index = self.chapters.findIndex((chapter) => chapter.id === newData.id);

      self.chapters[index] = { ...self.chapters[index], ...newData };
    },

    deleteChapter(chapterId) {
      const chapter = self.chapters.find((c) => chapterId === c.id);

      if (chapter) {
        for (const lesson of chapter.lessons) {
          destroy(lesson);
        }
        destroy(chapter);
      }
    },

    /**
     * Lesson model Actions
     */
    createLesson(lessonData) {
      const chapter = self.chapters.find((c) => c.id === lessonData.chapter.id);
      const newLesson = Lesson.create(lessonData);

      self.lessons.push(newLesson);
      chapter.lessons.push(newLesson.id);
    },

    changeLessonOrder(direction, lesson, index) {
      const lessons = lesson.chapter.lessons;

      switch (direction) {
        case "up":
          if (index <= 0) break;
          [lessons[index], lessons[index - 1]] = [lessons[index - 1], lessons[index]];
          break;
        case "down":
          if (index >= lessons.length - 1) break;
          [lessons[index], lessons[index + 1]] = [lessons[index + 1], lessons[index]];
          break;
      }
    },

    deleteLesson(lessonId) {
      const lesson = self.lessons.find((lesson) => lesson.id === lessonId);
      const chapterIndex = self.chapters.findIndex((chapter) => chapter.lessons.includes(lesson));

      self.chapters[chapterIndex].lessons = self.chapters[chapterIndex].lessons.filter(
        (lesson) => lesson.id !== lessonId,
      );

      destroy(lesson);
    },

    /**
     * Comment model Actions
     */
    setComments(comments) {
      self.comments = _.uniqBy(comments, (c) => c.id);
    },

    addComments(comments, mode = "push") {
      const newComments = [...self.comments];

      if (Array.isArray(comments)) {
        newComments[mode](...comments);
      } else {
        newComments[mode](comments);
      }

      self.comments = _.uniqBy(newComments, (c) => c.id);
    },

    createComment(commentData) {
      const newComment = Comment.create(commentData);

      if (!commentData.parent_id) {
        self.comments.unshift(newComment);
      } else {
        const parentComment = self.comments.find((c) => commentData.parent_id === c.id);

        if (parentComment) {
          parentComment.childComments.push(newComment);
        }
      }
    },

    updateComment(newComment) {
      if (!newComment.parent_id) {
        const i = self.comments.findIndex((comment) => comment.id === newComment.id);
        self.comments[i] = { ...self.comments[i], ...newComment };
      } else {
        const i = self.comments.findIndex((comment) => comment.id === newComment.parent_id);
        const j = self.comments[i].childComments.findIndex((c) => c.id === newComment.id);
        self.comments[i].childComments[j] = { ...self.comments[i].childComments[j], ...newComment };
      }
    },

    deleteComment(commentId) {
      let comment = self.comments.find((c) => commentId === c.id);
      if (!comment) {
        for (const c of self.comments) {
          comment = c.childComments?.find((child) => child.id === commentId);
          if (comment) break;
        }
      }

      if (comment) {
        for (const childComment of comment.childComments) {
          destroy(childComment);
        }
        destroy(comment);
      }
    },
  }));
